import { Stack } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { IconHourglass } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import {
  GRID_COLS,
  GRID_ROWS,
  GridPosition,
  SLIDE_CONTENT_HEIGHT,
  SLIDE_CONTENT_WIDTH,
} from '../types'
import { DashboardWidget } from './types'
import { TitleInput } from './OptionsFormFields'
import { WidgetEmpty } from './WidgetShell'
import classes from './Countdown.module.css'

const GAP_PX = 32
const CELL_W = (SLIDE_CONTENT_WIDTH - (GRID_COLS - 1) * GAP_PX) / GRID_COLS
const CELL_H = (SLIDE_CONTENT_HEIGHT - (GRID_ROWS - 1) * GAP_PX) / GRID_ROWS

const Schema = z.object({
  title: z.string().default(''),
  start_at: z.string().default(''),
  target_at: z.string().default(''),
})

type Settings = z.infer<typeof Schema>

type Parts = { days: number; hours: number; minutes: number; seconds: number }

function diffParts(targetMs: number, nowMs: number): Parts {
  const diff = Math.max(0, targetMs - nowMs)
  const totalSeconds = Math.floor(diff / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return { days, hours, minutes, seconds }
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

/** Formats the start date for the "Starts on …" pre-start state. */
function formatStart(iso: string): string {
  const d = dayjs(iso)
  return d.hour() === 0 && d.minute() === 0
    ? d.format('MMM D, YYYY')
    : d.format('MMM D, YYYY · h:mm A')
}

type Sizes = {
  padding: number
  rowGap: number
  unitGap: number
  title: number
  value: number
  label: number
  labelMargin: number
  startDate: number
}

function computeSizes(position: GridPosition): Sizes {
  const boxW = position.colSpan * CELL_W + (position.colSpan - 1) * GAP_PX
  const boxH = position.rowSpan * CELL_H + (position.rowSpan - 1) * GAP_PX
  const padding = Math.round(Math.min(boxW, boxH) * 0.025)
  const availW = boxW - padding * 2
  const availH = boxH - padding * 2

  // Row is 4 numbers (up to "DDD : HH : MM : SS") + 3 separators. At Barlow
  // 700 a digit is ~0.58× its font-size wide and a ":" ~0.28×, so the row is
  // ≈ 6.1 × value-font + 6 × unit-gap. We size `value` to fill ~93% of width.
  const value = Math.min(availW * 0.14, availH * 0.55)
  const title = Math.min(availW * 0.05, availH * 0.17)
  const label = Math.min(availW * 0.024, availH * 0.08)
  // "Starts on" date string is ~11 chars; sized smaller than the digit row
  // so a date like "Mar 1, 2026" fits comfortably without ellipsis.
  const startDate = Math.min(availW * 0.09, availH * 0.32)
  const unitGap = Math.max(6, Math.round(value * 0.1))
  const rowGap = Math.round(padding * 0.6)
  const labelMargin = Math.round(value * 0.06)

  return {
    padding,
    rowGap,
    unitGap,
    title: Math.round(title),
    value: Math.round(value),
    label: Math.round(label),
    labelMargin,
    startDate: Math.round(startDate),
  }
}

function Unit({
  value,
  label,
  sizes,
}: {
  value: string
  label: string
  sizes: Sizes
}) {
  return (
    <div className={classes.unit}>
      <div className={classes.value} style={{ fontSize: sizes.value }}>
        {value}
      </div>
      <div
        className={classes.label}
        style={{ fontSize: sizes.label, marginTop: sizes.labelMargin }}
      >
        {label}
      </div>
    </div>
  )
}

function Component({
  settings,
  position,
}: {
  settings: Settings
  position: GridPosition
}) {
  const [now, setNow] = useState(() => Date.now())
  const sizes = useMemo(() => computeSizes(position), [position])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const boxStyle = { padding: sizes.padding, gap: sizes.rowGap }

  if (!settings.target_at) {
    return (
      <div className={classes.box} style={boxStyle}>
        <WidgetEmpty message="Pick a target date/time" />
      </div>
    )
  }

  const targetMs = new Date(settings.target_at).getTime()
  if (Number.isNaN(targetMs)) {
    return (
      <div className={classes.box} style={boxStyle}>
        <WidgetEmpty message="Invalid target date" />
      </div>
    )
  }

  // Before the optional start date the countdown is irrelevant — show a
  // static "Starts on …" instead, then flip to the live countdown.
  if (settings.start_at) {
    const startMs = new Date(settings.start_at).getTime()
    if (!Number.isNaN(startMs) && now < startMs) {
      return (
        <div className={classes.box} style={boxStyle}>
          {settings.title && (
            <div className={classes.title} style={{ fontSize: sizes.title }}>
              {settings.title}
            </div>
          )}
          <div className={classes.starts}>
            <div className={classes.label} style={{ fontSize: sizes.label }}>
              STARTS ON
            </div>
            <div
              className={classes.startDate}
              style={{
                fontSize: sizes.startDate,
                marginTop: sizes.labelMargin,
              }}
            >
              {formatStart(settings.start_at)}
            </div>
          </div>
        </div>
      )
    }
  }

  const { days, hours, minutes, seconds } = diffParts(targetMs, now)

  return (
    <div className={classes.box} style={boxStyle}>
      {settings.title && (
        <div className={classes.title} style={{ fontSize: sizes.title }}>
          {settings.title}
        </div>
      )}
      <div className={classes.row} style={{ gap: sizes.unitGap }}>
        <Unit value={String(days)} label="DAYS" sizes={sizes} />
        <div className={classes.sep} style={{ fontSize: sizes.value }}>
          :
        </div>
        <Unit value={pad2(hours)} label="HOURS" sizes={sizes} />
        <div className={classes.sep} style={{ fontSize: sizes.value }}>
          :
        </div>
        <Unit value={pad2(minutes)} label="MINUTES" sizes={sizes} />
        <div className={classes.sep} style={{ fontSize: sizes.value }}>
          :
        </div>
        <Unit value={pad2(seconds)} label="SECONDS" sizes={sizes} />
      </div>
    </div>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  const startAfterTarget =
    !!value.start_at &&
    !!value.target_at &&
    new Date(value.start_at).getTime() >= new Date(value.target_at).getTime()
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <DateTimePicker
        label="Start date / time"
        placeholder="Optional — leave blank for none"
        description="Until this date the widget shows “Starts on …” instead of the countdown"
        value={value.start_at || null}
        onChange={(v) => patch({ start_at: v ?? '' })}
        error={
          startAfterTarget
            ? 'Start date should be before the target date'
            : undefined
        }
        clearable
      />
      <DateTimePicker
        label="Target date / time"
        placeholder="Pick a target"
        value={value.target_at || null}
        onChange={(v) => patch({ target_at: v ?? '' })}
        clearable
      />
    </Stack>
  )
}

export const CountdownWidget: DashboardWidget<Settings> = {
  type: 'countdown',
  label: 'Countdown',
  description: 'Counts down to a target date/time',
  icon: IconHourglass,
  minW: 2,
  minH: 1,
  maxW: 6,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
